// SliderRange.tsx
import { FunctionComponent, useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import Colors from "../global/Colors";
import Fonts from "../global/Fonts";
import { Slider as MUISlider } from "@mui/material";

const SliderRoot = styled.div<{ $disabled: boolean; $orientation: 'horizontal' | 'vertical' }>`
    position: relative;
    display: flex;
    flex-direction: ${props => props.$orientation === 'vertical' ? 'column' : 'row'};
    align-items: center;
    justify-content: center;
    width: ${props => props.$orientation === 'vertical' ? 'auto' : '100%'};
    height: ${props => props.$orientation === 'vertical' ? '100%' : 'auto'};
    padding: ${props => props.$orientation === 'vertical' ? '6px 0' : '0 6px'};
    overflow: visible;
    pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
    user-select: none;
    cursor: ${props => props.$disabled ? 'default' : 'pointer'};
`;

// Track Background (Rail)
const Rail = styled.div<{ $size?: 'default' | 'small'; $colorMode?: 'light' | 'dark' }>`
    position: absolute;
    width: 100%;
    height: ${props => props.$size === 'small' ? '3px' : '4px'};
    background-color: ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2};
    border-radius: 2px;
    top: 50%;
    transform: translateY(-50%);
`;

// Selected Range Track
const RangeTrack = styled.div<{ $left: number; $width: number; $size?: 'default' | 'small'; $colorMode?: 'light' | 'dark' }>`
    position: absolute;
    left: ${props => props.$left}%;
    width: ${props => props.$width}%;
    height: ${props => props.$size === 'small' ? '3px' : '4px'};
    background-color: ${props => props.$colorMode === 'dark' ? Colors.grey5 : Colors.grey4};
    border-radius: 2px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
`;

// Custom Range Knob (Start/End)
const RangeKnob = styled.div<{
    $left: number; 
    $type: 'start' | 'end';
    $size?: 'default' | 'small';
    $colorMode?: 'light' | 'dark'; 
    $disabled: boolean; 
    $isHovering: boolean;
    $shouldHide: boolean;
    $miniKnob?: boolean;
    $isOverlapping?: boolean;
}>`
    position: absolute;
    left: ${props => props.$left}%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: ${props => props.$miniKnob ? '4px' : '6px'};
    height: ${props => props.$miniKnob ? '12px' : (props.$size === 'small' ? '12px' : '16px')};
    background-color: transparent;
    border-radius: ${props => props.$type === 'start' ? '1px 0 0 1px' : '0 1px 1px 0'};
    z-index: 5;
    cursor: ${props => props.$disabled ? 'default' : 'pointer'};
    
    // Style the "visual" part as borders to match original look
    border-top: 2px solid ${props => props.$disabled ? Colors.grey5 : (props.$colorMode === 'dark' ? Colors.grey3 : Colors.grey6)};
    border-bottom: 2px solid ${props => props.$disabled ? Colors.grey5 : (props.$colorMode === 'dark' ? Colors.grey3 : Colors.grey6)};
    ${props => props.$type === 'start' 
        ? `border-left: 4px solid ${props.$disabled ? Colors.grey5 : (props.$colorMode === 'dark' ? Colors.grey3 : Colors.grey6)}; border-right: none;`
        : `border-right: 4px solid ${props.$disabled ? Colors.grey5 : (props.$colorMode === 'dark' ? Colors.grey3 : Colors.grey6)}; border-left: none;`
    }

    opacity: ${props => (props.$shouldHide && !props.$isHovering) ? '0' : '1'};
    transition: opacity 0.2s ease;
    
    // Interaction Priority: If overlapping the Main Knob, disable pointer events so the Main Knob (MUI Slider usually) gets the event.
    pointer-events: ${props => (props.$isOverlapping || props.$disabled) ? 'none' : 'auto'};

    &:hover {
        border-top-color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.grey7};
        border-bottom-color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.grey7};
        ${props => props.$type === 'start' 
            ? `border-left-color: ${props.$colorMode === 'dark' ? Colors.white : Colors.grey7};` 
            : `border-right-color: ${props.$colorMode === 'dark' ? Colors.white : Colors.grey7};`
        }
    }
`;

// Main Knob Styled Slider (MUI)
// We only use this for the Main Knob now. The rail and track are hidden because we draw them manually.
const StyledMainSlider = styled(MUISlider)<{ 
  $colorMode?: 'light' | 'dark'; 
  $size?: 'default' | 'small';
  $hideKnob: boolean;
  $isHovering: boolean;
  $miniKnob?: boolean;
}>`
  && {
    position: absolute;
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    z-index: 10; // Main knob is always on top

    // Hide default rail and track
    .MuiSlider-rail, .MuiSlider-track {
      display: none; 
    }
    
    .MuiSlider-thumb {
      width: ${props => props.$miniKnob ? '6px' : (props.$size === 'small' ? '12px' : '16px')};
      height: ${props => props.$miniKnob ? '12px' : (props.$size === 'small' ? '12px' : '16px')};
      border-radius: ${props => props.$miniKnob ? '3px' : '50%'};
      background-color: ${props => props.$colorMode === 'dark' ? Colors.grey3 : Colors.grey6};
      box-shadow: none;
      transition: opacity 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
      opacity: ${props => (props.$hideKnob && !props.$isHovering) ? '0' : '1'};

      &:hover, &.Mui-focusVisible, &.Mui-active {
        background-color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.grey7};
        box-shadow: ${props => (props.$size === 'small' || props.$miniKnob) ? 'none' : `0 0 0 6px ${props.$colorMode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.16)'}`};
      }
    }

    .MuiSlider-valueLabel {
      background-color: ${props => props.$colorMode === 'dark' ? Colors.black : Colors.white};
      color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.black};
      font-family: ${Fonts.lexendMedium.fontFamily};
      font-weight: ${Fonts.lexendMedium.fontWeight};
      font-size: ${props => props.$size === 'small' ? '12px' : '14px'};
      border-radius: 6px;
      border: 1px solid ${props => props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey2};
      box-shadow: 0px 2px 4px ${props => props.$colorMode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
    }
  }
`;

export interface RangeSliderProps {
  value: [number, number, number]; // [startRange, currentValue, endRange]
  min: number;
  max: number;
  step?: number;
  tick?: boolean;
  colorMode?: 'dark' | 'light';
  disabled?: boolean;
  size?: 'default' | 'small';
  onChange: (value: [number, number, number]) => void;
  marks?: boolean | { value: number; label?: string }[];
  showLabel?: boolean;
  hideKnobUntilHover?: boolean;
  hideRangeKnobsUntilHover?: boolean;
  miniKnob?: boolean;
  orientation?: 'horizontal' | 'vertical'; // Basic support, mostly horizontal assumed for custom knobs logic
}

const SliderRange: FunctionComponent<RangeSliderProps> = ({
  value, 
  min,
  max,
  colorMode = 'light',
  disabled = false,
  onChange,
  step = 1,
  marks,
  showLabel = true,
  size = 'default',
  hideKnobUntilHover = false,
  hideRangeKnobsUntilHover = false,
  miniKnob = false,
  orientation = 'horizontal'
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Destructure value for easier access
  const [startVal, currentVal, endVal] = value;

  // Calculate percentages for positioning
  const totalRange = max - min;
  
  // Ensure that if the range logic results in 0, we still default visual markers to the track ends
  const startPct = totalRange === 0 ? 0 : ((startVal - min) / totalRange) * 100;
  const endPct = totalRange === 0 ? 100 : ((endVal - min) / totalRange) * 100;
  
  // Width of the selected range track
  const rangeWidthPct = endPct - startPct;

  const handleMainChange = (_: unknown, newVal: number | number[]) => {
      const newCurrent = newVal as number;
      // Clamp main knob between current start and end
      const clampedCurrent = Math.min(Math.max(newCurrent, startVal), endVal);
      onChange([startVal, clampedCurrent, endVal]);
  };

  const handleRootClick = (e: React.MouseEvent) => {
      if (disabled || !containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const clientX = e.clientX;
      
      let pct = (clientX - rect.left) / rect.width;
      pct = Math.max(0, Math.min(1, pct));
      
      let rawVal = min + (pct * totalRange);
      
      if (step) {
          rawVal = Math.round(rawVal / step) * step;
      }

      const clampedCurrent = Math.min(Math.max(rawVal, startVal), endVal);
      onChange([startVal, clampedCurrent, endVal]);
  };

  // Custom Drag Handling
  const handleDragStart = useCallback((type: 'start' | 'end') => (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled) return;
      e.stopPropagation(); // prevent triggering other things
      
      const moveHandler = (moveEvent: MouseEvent | TouchEvent) => {
          if (!containerRef.current) return;
          
          const rect = containerRef.current.getBoundingClientRect();
          const clientX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
          
          let pct = (clientX - rect.left) / rect.width;
          pct = Math.max(0, Math.min(1, pct));
          
          let rawVal = min + (pct * totalRange);
          
          // Snap to step
          if (step) {
              rawVal = Math.round(rawVal / step) * step;
          }
          
          // Clamp & Logic
          let newStart = startVal;
          let newEnd = endVal;
          let newCurrent = currentVal;

          if (type === 'start') {
              // Start cannot go past End
              newStart = Math.min(rawVal, endVal);
              // Push Current if needed
              if (newCurrent < newStart) newCurrent = newStart;
          } else {
              // End cannot go before Start
              newEnd = Math.max(rawVal, startVal);
              // Push Current if needed
              if (newCurrent > newEnd) newCurrent = newEnd;
          }

          onChange([newStart, newCurrent, newEnd]);
      };

      const upHandler = () => {
          document.removeEventListener('mousemove', moveHandler);
          document.removeEventListener('mouseup', upHandler);
          document.removeEventListener('touchmove', moveHandler);
          document.removeEventListener('touchend', upHandler);
      };

      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
      document.addEventListener('touchmove', moveHandler);
      document.addEventListener('touchend', upHandler);
  }, [disabled, min, totalRange, step, startVal, endVal, currentVal, onChange]);


  const startOverlaps = Math.abs(startVal - currentVal) < (step || 1) * 0.1; // approximate equality check
  const endOverlaps = Math.abs(endVal - currentVal) < (step || 1) * 0.1;

  return (
    <SliderRoot 
      ref={containerRef}
      $disabled={disabled}
      $orientation={orientation}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={handleRootClick}
    >
        {/* Visual Rail */}
        <Rail $size={size} $colorMode={colorMode} />
        
        {/* Visual Selected Range */}
        <RangeTrack 
            $left={startPct} 
            $width={rangeWidthPct} 
            $size={size} 
            $colorMode={colorMode} 
        />

        {/* Custom Start Knob */}
        <RangeKnob
            $left={startPct}
            $type="start"
            $size={size}
            $colorMode={colorMode}
            $disabled={disabled}
            $isHovering={isHovering}
            $shouldHide={hideRangeKnobsUntilHover}
            $miniKnob={miniKnob}
            $isOverlapping={startOverlaps}
            onMouseDown={handleDragStart('start')}
            onTouchStart={handleDragStart('start')}
            data-index="0"
        />

        {/* Custom End Knob */}
        <RangeKnob
            $left={endPct}
            $type="end"
            $size={size}
            $colorMode={colorMode}
            $disabled={disabled}
            $isHovering={isHovering}
            $shouldHide={hideRangeKnobsUntilHover}
            $miniKnob={miniKnob}
            $isOverlapping={endOverlaps}
            onMouseDown={handleDragStart('end')}
            onTouchStart={handleDragStart('end')}
            data-index="2"
        />

        {/* Main Knob (MUISlider) - Only manages the current value */}
        <StyledMainSlider
            value={currentVal} 
            min={min}
            max={max}
            step={step}
            marks={marks}
            disabled={disabled}
            onChange={handleMainChange}
            valueLabelDisplay={showLabel ? "auto" : "off"} 
            $colorMode={colorMode}
            $size={size}
            $hideKnob={hideKnobUntilHover}
            $isHovering={isHovering}
            $miniKnob={miniKnob}
            orientation={orientation}
      />
    </SliderRoot>
  );
};

export default SliderRange;